import { useState } from "react";
import { TextField, MenuItem } from "@mui/material";
import { useSnackbar } from "notistack";
import QuickCreateModal from "../common/QuickCreateModal";
import { useCreateProviderMutation } from "../../features/masterdata/masterdataApi";
import type { Provider, ProviderCreate, ProviderType } from "../../features/masterdata/types";

const PROVIDER_TYPES: { value: ProviderType; label: string }[] = [
  { value: "bus_company", label: "Bus company" },
  { value: "taxi", label: "Taxi" },
  { value: "private_driver", label: "Private driver" },
  { value: "other", label: "Other" },
];

export interface QuickCreateProviderProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (provider: Provider) => void;
}

export default function QuickCreateProvider({ open, onClose, onSuccess }: QuickCreateProviderProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [createProvider, { isLoading }] = useCreateProviderMutation();

  const [name, setName] = useState("");
  const [type, setType] = useState<ProviderType>("bus_company");

  const handleSubmit = async (): Promise<Provider> => {
    const payload: ProviderCreate = { name: name.trim(), type, is_active: true };
    const created = await createProvider(payload).unwrap();
    enqueueSnackbar("Provider created", { variant: "success" });
    return created;
  };

  return (
    <QuickCreateModal<Provider>
      open={open}
      onClose={onClose}
      title="New provider"
      onSubmit={handleSubmit}
      onSuccess={(p) => {
        setName("");
        setType("bus_company");
        onSuccess(p);
      }}
      submitDisabled={!name.trim()}
      loading={isLoading}
      submitLabel="Save & select"
    >
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
        autoFocus
        placeholder="e.g. City Taxi Co"
      />
      <TextField
        label="Type"
        select
        value={type}
        onChange={(e) => setType(e.target.value as ProviderType)}
        fullWidth
      >
        {PROVIDER_TYPES.map((t) => (
          <MenuItem key={t.value} value={t.value}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>
    </QuickCreateModal>
  );
}
